// ModalManager.ts

export interface ModalOptions {
  content: string;
  modalId: string;
  modalAriaLabel?: string;
  triggerElement?: HTMLElement;
  dataClass?: string | null;
  onClose?: () => void;
}

export class ModalManager {
  private modalElement: HTMLDivElement | null = null;
  private closeButtonLink: HTMLAnchorElement | null = null;
  private onCloseCallback?: () => void;
  private triggerElement?: HTMLElement;
  private openAnimationDuration = 150; // ms

  // フォーカス可能要素リスト
  private focusableElements: HTMLElement[] = [];
  private firstFocusableElement: HTMLElement | null = null;
  private lastFocusableElement: HTMLElement | null = null;

  public showModal(options: ModalOptions): void {
    const {
      content,
      onClose,
      modalId,
      modalAriaLabel,
      triggerElement,
      dataClass,
    } = options;
    this.onCloseCallback = onClose;
    this.triggerElement = triggerElement || undefined;

    // モーダル全体を囲むdiv
    this.modalElement = document.createElement('div');
    this.modalElement.classList.add('ist-js__modalView');
    if (dataClass) {
      this.modalElement.classList.add(dataClass);
      // data-class にも同じクラスを付与 閉じる時に削除するため
      this.modalElement.setAttribute('data-class', dataClass);
    }
    this.modalElement.setAttribute('role', 'dialog');
    this.modalElement.setAttribute('aria-modal', 'true');
    this.modalElement.setAttribute('aria-hidden', 'false');
    this.modalElement.setAttribute('id', modalId);

    // aria-labelをモーダル本体に付与（modalAriaLabelがあればその値を設定）
    if (modalAriaLabel) {
      this.modalElement.setAttribute('aria-label', modalAriaLabel);
    }

    // メインのコンテナ
    const overlay = document.createElement('div');
    overlay.classList.add('ist-js__modalMain');

    // 内側のコンテナ
    const inner = document.createElement('div');
    inner.classList.add('ist-js__modalInner');

    // クローズボタンコンテナ
    const closeButtonContainer = document.createElement('div');
    closeButtonContainer.classList.add('ist-js__modalCloseButton');

    // クローズボタン
    this.closeButtonLink = document.createElement('a');
    this.closeButtonLink.classList.add('ist-js__modalCloseButtonLink');
    this.closeButtonLink.setAttribute('href', '#');
    this.closeButtonLink.setAttribute('aria-label', 'モーダルを閉じる');
    this.closeButtonLink.addEventListener('click', (e) => {
      e.preventDefault();
      this.hideModal();
    });
    closeButtonContainer.appendChild(this.closeButtonLink);

    // モーダルコンテンツを挿入
    const mainContent = document.createElement('div');
    mainContent.classList.add('ist-js__modalContent');
    mainContent.innerHTML = content;

    inner.appendChild(closeButtonContainer);
    inner.appendChild(mainContent);
    overlay.appendChild(inner);
    this.modalElement.appendChild(overlay);
    document.body.appendChild(this.modalElement);

    // jQuery の外部サイトモーダル処理の初期化
    if (typeof window.initExternalModalScripts === 'function') {
      window.initExternalModalScripts(mainContent);
    }

    // キーボード操作（Escで閉じる）のためのリスナー追加
    document.addEventListener('keydown', this.handleGlobalKeyDown);

    // 外側クリックで閉じる処理
    this.modalElement.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const isInsideContent = !!target.closest('.ist-js__modalContent');
      const isCloseButton = !!target.closest('.ist-js__modalCloseButtonLink');
      // コンテンツ内では何もしない。クローズボタンやコンテンツ外(オーバーレイ)なら閉じる
      if (!isInsideContent && !isCloseButton) {
        this.hideModal();
      }
    });

    // コンテンツ内に .ist-js__modalClose があれば、その要素をクリックしたらモーダルを閉じる
    this.modalElement.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const isCloseButton = !!target.closest('.ist-js__modalClose');
      // コンテンツ内に .ist-js__modalClose があれば、その要素をクリックしたらモーダルを閉じる
      if (isCloseButton) {
        event.preventDefault();
        this.hideModal();
      }
    });

    // モーダル内のフォーカス可能要素を取得
    this.setFocusableElements();

    // 初期フォーカスをクローズボタンまたは先頭のフォーカス可能要素へ移動
    if (this.firstFocusableElement) {
      this.firstFocusableElement.focus();
    } else {
      this.closeButtonLink.focus();
    }

    // 次フレームでopenクラスを付与してアニメーション再生
    requestAnimationFrame(() => {
      if (this.modalElement) {
        this.modalElement.classList.add('ist-js__modal--open');
      }
    });
  }

  public hideModal(): void {
    if (!this.modalElement) return;

    // aria-hiddenを先に設定
    this.modalElement.setAttribute('aria-hidden', 'true');

    // data-class を削除
    const removeClassText = this.modalElement.getAttribute('data-class');
    if (removeClassText) {
      this.modalElement.classList.remove(removeClassText);
      this.modalElement.removeAttribute('data-class');
    }

    requestAnimationFrame(() => {
      if (!this.modalElement) return;
      this.modalElement.classList.remove('ist-js__modal--open');

      setTimeout(() => {
        if (this.modalElement) {
          this.modalElement.remove();
          this.modalElement = null;
        }

        document.removeEventListener('keydown', this.handleGlobalKeyDown);

        // モーダルを閉じた後、元のトリガー要素へフォーカスを戻す
        if (
          this.triggerElement &&
          typeof this.triggerElement.focus === 'function'
        ) {
          this.triggerElement.focus();
        }

        if (typeof this.onCloseCallback === 'function') {
          this.onCloseCallback();
          this.onCloseCallback = undefined;
        }
      }, this.openAnimationDuration);
    });
  }

  private handleGlobalKeyDown = (event: KeyboardEvent): void => {
    // Escキー処理
    if (event.key === 'Escape') {
      this.hideModal();
      return;
    }

    // Tabキーでフォーカスがモーダル外へ移動しないようにする
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  };

  private setFocusableElements(): void {
    if (!this.modalElement) return;

    const focusableSelectors = [
      'a[href]',
      'area[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      'iframe',
      'object',
      'embed',
      '[contenteditable]',
      '[tabindex]:not([tabindex="-1"])',
    ];

    const elements = this.modalElement.querySelectorAll<HTMLElement>(
      focusableSelectors.join(',')
    );
    this.focusableElements = Array.from(elements).filter(
      (el) =>
        el.offsetWidth > 0 ||
        el.offsetHeight > 0 ||
        el.getClientRects().length > 0
    );

    // 最初と最後のフォーカス可能要素を取得
    this.firstFocusableElement =
      this.focusableElements[0] || this.closeButtonLink;
    this.lastFocusableElement =
      this.focusableElements[this.focusableElements.length - 1] ||
      this.closeButtonLink;
  }

  private trapFocus(event: KeyboardEvent): void {
    if (
      this.focusableElements.length === 0 ||
      !this.firstFocusableElement ||
      !this.lastFocusableElement
    ) {
      // フォーカスできる要素がなければ何もしない
      return;
    }

    const isShiftPressed = event.shiftKey;
    const activeElement = document.activeElement;

    if (!activeElement) return;

    if (!this.modalElement || !this.modalElement.contains(activeElement)) {
      // モーダル外にフォーカスがある場合は最初の要素にフォーカスを戻す
      this.firstFocusableElement.focus();
      event.preventDefault();
      return;
    }

    if (isShiftPressed) {
      // Shift+Tabで最初の要素に戻ってしまう場合
      if (activeElement === this.firstFocusableElement) {
        this.lastFocusableElement.focus();
        event.preventDefault();
      }
    } else {
      // Tabで最後の要素を超える場合
      if (activeElement === this.lastFocusableElement) {
        this.firstFocusableElement.focus();
        event.preventDefault();
      }
    }
  }
}
